import * as React from 'react';

type Updater<V> = (prev: V | null | undefined) => V | null | undefined;
type SetterArg<V> = V | Updater<V>;

const getLocalStorageItem = (key: string): string | null => window.localStorage.getItem(key);

const setLocalStorageItem = (key: string, value: string): void => window.localStorage.setItem(key, value);

const removeLocalStorageItem = (key: string): void => window.localStorage.removeItem(key);

const parse = <T,>(raw: string | null, fallback: T | null | undefined): T | null | undefined => {
  if (raw == null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

function isUpdater<V>(v: SetterArg<V>): v is Updater<V> {
  return typeof v === 'function';
}

const makeSubscribe = (key: string) => (onStoreChange: () => void) => {
  const onLocal = (e: Event) => {
    const detailKey = (e as CustomEvent)?.detail?.key as string | undefined;
    if (!detailKey || detailKey === key) onStoreChange();
  };

  window.addEventListener('local-storage', onLocal as EventListener);

  return () => {
    window.removeEventListener('local-storage', onLocal as EventListener);
  };
};

const getServerSnapshot = () => {
  throw Error('useLocalStorage is a client-only hook');
};

/**
 * @name useLocalStorage
 * Hook for localStorage with same-tab synchronization.
 * Returns: [value, setValue, remove]
 */
export function useLocalStorage<V = unknown>(
  key: string,
  initialValue?: V | null
): [V | null | undefined, (v: SetterArg<V>) => void, () => void] {
  const getSnapshot = React.useCallback(() => getLocalStorageItem(key), [key]);

  const raw = React.useSyncExternalStore(makeSubscribe(key), getSnapshot, getServerSnapshot);

  const value = parse<V>(raw, initialValue);

  const setState = React.useCallback(
    (next: SetterArg<V>) => {
      try {
        const current = parse<V>(getLocalStorageItem(key), initialValue);
        const resolved = isUpdater(next) ? next(current) : next;

        if (resolved == null) {
          removeLocalStorageItem(key);
        } else {
          const stringified = JSON.stringify(resolved);
          setLocalStorageItem(key, stringified);
        }

        window.dispatchEvent(new CustomEvent('local-storage', { detail: { key } }));
      } catch (e) {
        console.warn(e);
      }
    },
    [key, initialValue]
  );

  React.useEffect(() => {
    const existing = getLocalStorageItem(key);
    if (existing === null && typeof initialValue !== 'undefined') {
      try {
        setLocalStorageItem(key, JSON.stringify(initialValue));
        window.dispatchEvent(new CustomEvent('local-storage', { detail: { key } }));
      } catch (e) {
        console.warn(e);
      }
    }
  }, [key, initialValue]);

  const removeValue = React.useCallback(() => {
    removeLocalStorageItem(key);
    window.dispatchEvent(new CustomEvent('local-storage', { detail: { key } }));
  }, [key]);

  return [value, setState, removeValue];
}
